/**
 * @fileoverview Report Tabs
 * @description Section switcher that mirrors the tab strip of the Scout consolidated
 * HTML report: a lead panel (summary or comparison) followed by one tab per embedded
 * detail report.
 * @module features/ScoutEval/components/ReportTabs
 */

import React from 'react';
import { Box } from '@mui/material';

/**
 * Render the report section tabs.
 * @param {Object} props - Tabs, active tab id, and change handler.
 * @returns {JSX.Element} Tab strip.
 */
export function ReportTabs({ tabs, activeId, onChange }) {
  return (
    <Box className="scout-report-tabs" role="tablist" aria-label="Report sections">
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`scout-tab-${tab.id}`}
            aria-controls={`scout-panel-${tab.id}`}
            aria-selected={isActive}
            className={isActive ? 'scout-report-tab is-active' : 'scout-report-tab'}
            onClick={() => onChange(tab.id)}
          >
            {tab.label}
          </button>
        );
      })}
    </Box>
  );
}

/**
 * Render the panel body for the active tab.
 * @param {Object} props - Tab id and panel content.
 * @returns {JSX.Element} Tab panel.
 */
export function ReportPanel({ tabId, children }) {
  return (
    <Box
      className="scout-report-panel"
      role="tabpanel"
      id={`scout-panel-${tabId}`}
      aria-labelledby={`scout-tab-${tabId}`}
    >
      {children}
    </Box>
  );
}

export default ReportTabs;
